import { useState } from 'react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { IconChevronDown, IconCalendar } from '@tabler/icons-react';
import { Screen, EmptyCase } from '../../components';
import { TestResult } from './components';
import { useGetUserTests } from '../../services/userTest';
import { type IUserTest } from '../../types/userTest';
import { Skeleton } from 'antd';
import clsx from 'clsx';

interface Parsed {
  date: string
  tests: IUserTest[]
}

const ResultsPage = () => {
  const { loading, userTests } = useGetUserTests();
  const [openSections, setOpenSections] = useState<Set<number>>(new Set([0])); // First section open by default

  const transformedArray = (userTests || []).reduce((acc: Parsed[], obj: IUserTest) => {
    // Format the date as "month day" (e.g., "January 23")
    const formattedDate = format(new Date(obj.date), 'MMMM yyyy', { locale: es });

    // Check if there is an existing entry with the same date
    const existingEntry = acc.find(entry => entry.date === formattedDate);

    // If there is an existing entry, add the current test to its tests array
    if (existingEntry) {
      existingEntry.tests.push({
        _id: obj._id,
        title: obj.title,
        date: obj.date,
        correctQuestions: obj.correctQuestions,
        numberOfquestions: obj.numberOfquestions,
        failedQuestions: obj.failedQuestions
      });
    } else {
      // If there is no existing entry, create a new entry with the current test
      acc.push({
        date: formattedDate,
        tests: [{
          _id: obj._id,
          title: obj.title,
          date: obj.date,
          correctQuestions: obj.correctQuestions,
          numberOfquestions: obj.numberOfquestions,
          failedQuestions: obj.failedQuestions
        }]
      });
    }

    return acc;
  }, []);

  const toggleSection = (index: number) => {
    setOpenSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  if (loading) {
    return (
      <Screen id="results" title="Mis resultados">
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className={clsx(
                'rounded-xl border p-4',
                'bg-white border-gray-200',
                'dark:bg-surface-dark dark:border-borders'
              )}
            >
              <Skeleton active paragraph={{ rows: 2 }} />
            </div>
          ))}
        </div>
      </Screen>
    );
  }

  if (!transformedArray || transformedArray.length === 0) {
    return (
      <Screen id="results" title="Mis resultados">
        <EmptyCase text="No hay resultados que mostrar" />
      </Screen>
    );
  }

  return (
    <Screen id="results" title="Mis resultados">
      <div className="space-y-4">
        {transformedArray.map((item, index) => {
          const isOpen = openSections.has(index);

          return (
            <div
              key={index}
              className={clsx(
                'rounded-xl border overflow-hidden transition-all duration-300',
                'bg-white border-gray-200 shadow-sm',
                'dark:bg-surface-dark dark:border-borders dark:shadow-lg',
                'hover:shadow-md dark:hover:shadow-xl'
              )}
            >
              {/* Header */}
              <button
                onClick={() => toggleSection(index)}
                className={clsx(
                  'w-full flex items-center justify-between p-4 transition-colors duration-300',
                  'hover:bg-gray-50 dark:hover:bg-surface-medium',
                  'focus:outline-none focus:ring-2 focus:ring-inset',
                  'focus:ring-blue-500 dark:focus:ring-blue-400'
                )}
              >
                <div className="flex items-center gap-3">
                  <div className={clsx(
                    'p-2 rounded-lg',
                    'bg-blue-100 dark:bg-blue-900/30'
                  )}>
                    <IconCalendar
                      size={20}
                      className="text-blue-600 dark:text-blue-400"
                    />
                  </div>
                  <h3 className={clsx(
                    'text-lg font-semibold capitalize transition-colors duration-300',
                    'text-gray-900 dark:text-white'
                  )}>
                    {item.date}
                  </h3>
                  <span className={clsx(
                    'px-2.5 py-0.5 rounded-full text-xs font-medium',
                    'bg-gray-100 text-gray-600',
                    'dark:bg-surface-medium dark:text-gray-400'
                  )}>
                    {item.tests.length} {item.tests.length === 1 ? 'test' : 'tests'}
                  </span>
                </div>
                <IconChevronDown
                  size={20}
                  className={clsx(
                    'transition-transform duration-300 text-gray-400 dark:text-gray-500',
                    isOpen && 'rotate-180'
                  )}
                />
              </button>

              {/* Content */}
              {isOpen && (
                <div className={clsx(
                  'border-t p-4 space-y-3',
                  'border-gray-200 dark:border-borders',
                  'animate-fadeInUp'
                )}>
                  {item.tests.map((test, idx) => (
                    <TestResult
                      key={test._id || idx}
                      testId={test._id}
                      name={test.title}
                      questions={test.numberOfquestions}
                      answered={test.correctQuestions}
                      success={test.correctQuestions > (test.numberOfquestions / 2)}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Screen>
  );
};

export { ResultsPage };
